import { useCallback, useRef, useState, type DragEvent, type ChangeEvent } from 'react';
import toast from 'react-hot-toast';
import { validateUploadFile } from '@/utils/fileValidation';
import { ACCEPTED_FILE_EXTENSIONS } from '@/constants/upload';

interface UseFileDropzoneOptions {
  onFileAccepted: (file: File) => void;
}

export function useFileDropzone({ onFileAccepted }: UseFileDropzoneOptions) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const acceptAttr = ACCEPTED_FILE_EXTENSIONS.join(',');

  const processFile = useCallback(
    (file: File | undefined) => {
      if (!file) return;
      const result = validateUploadFile(file);
      if (!result.valid) {
        toast.error(result.error ?? 'This file cannot be uploaded.');
        return;
      }
      onFileAccepted(file);
    },
    [onFileAccepted],
  );

  const openFilePicker = useCallback(() => inputRef.current?.click(), []);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      processFile(event.target.files?.[0]);
      // Reset so selecting the same file again still fires onChange
      event.target.value = '';
    },
    [processFile],
  );

  const handleDragOver = useCallback((event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      event.preventDefault();
      setIsDragging(false);
      processFile(event.dataTransfer.files?.[0]);
    },
    [processFile],
  );

  return {
    inputRef,
    acceptAttr,
    isDragging,
    openFilePicker,
    handleInputChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  };
}
